// src/contexts/LanguageContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

// Import translation files
import en from '../locales/en.json';
import sw from '../locales/sw.json';
import fr from '../locales/fr.json';
import id from '../locales/id.json';

const translations = { en, sw, fr, id };

// DEBUG: Log what's loaded
console.log('=== LANGUAGE CONTEXT DEBUG ===');
console.log('Loaded translations:', Object.keys(translations));
console.log('EN biocharToSoil exists:', !!en.biocharToSoil);
console.log('SW biocharToSoil exists:', !!sw.biocharToSoil);
console.log('EN biocharToSoil.tabs:', en.biocharToSoil?.tabs);
console.log('SW biocharToSoil.tabs:', sw.biocharToSoil?.tabs);

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const stored = localStorage.getItem('language') || 'en';
    console.log('Initial language from localStorage:', stored);
    return stored;
  });

  useEffect(() => {
    console.log('Language changed to:', language);
    localStorage.setItem('language', language);
  }, [language]);

  const changeLanguage = (code) => {
    console.log('changeLanguage called with:', code);
    if (translations[code]) {
      setLanguage(code);
    } else {
      console.warn('Translation not found for:', code);
    }
  };

  // Translation function
  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    // Fallback to English if translation not found
    if (value === undefined) {
      console.warn(`Translation missing for "${key}" in "${language}", falling back to EN`);
      value = translations.en;
      for (const k of keys) {
        value = value?.[k];
      }
    }
    
    // DEBUG: Log first few translation lookups
    if (key.includes('biocharToSoil')) {
      console.log(`t("${key}") [${language}] =>`, value);
    }
    
    return value || key;
  };

  const value = {
    language,
    changeLanguage,
    t,
    availableLanguages: [
      { code: 'en', name: 'English', flag: '🇬🇧' },
      { code: 'sw', name: 'Kiswahili', flag: '🇰🇪' },
      { code: 'fr', name: 'Français', flag: '🇫🇷' },
      { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    // Return default values if used outside provider
    return { 
      language: 'en', 
      changeLanguage: () => {}, 
      t: (key) => key,
      availableLanguages: []
    };
  }
  return context;
}

export default LanguageContext;